import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useClientFlags } from '@/hooks/useClientFlags';
import { setByLine, shortPerson } from '@/utils/clientFlags';
import { formatNzDate } from '@/utils/relativeTime';
import { ClientTagDialog, type ClientTagKind } from './ClientTagDialog';
import './clientFlags.css';

interface ClientFlagControlProps {
  /** `deploy-block` shows whether deploys are allowed. `inactive` shows whether the client is active. */
  kind: ClientTagKind;
  clientName: string;
  disabled?: boolean;
}

/**
 * One tag of one client: how it stands now, and the button that changes it.
 * The button opens the dialog, and the dialog does the writing. The Configs
 * page and the Update client config tool both show this.
 */
export function ClientFlagControl({ kind, clientName, disabled = false }: ClientFlagControlProps) {
  const flags = useClientFlags();
  const [open, setOpen] = useState(false);
  const loading = flags.status === 'loading';

  let state: string;
  let sub = '';
  let on = false;
  let button: string;
  if (kind === 'deploy-block') {
    const block = flags.deployBlock(clientName);
    on = Boolean(block);
    state = block ? `Do not deploy: ${block.reason}` : 'Allowed';
    sub = block ? setByLine(block) : '';
    button = block ? 'Allow deploys again' : 'Block deploys';
  } else {
    const tag = flags.inactive(clientName);
    on = Boolean(tag);
    const since = tag ? formatNzDate(tag.setAt) : '';
    state = tag ? `Inactive${since ? ` since ${since}` : ''}${tag.reason ? `: ${tag.reason}` : ''}` : 'Active';
    sub = tag ? `Set by ${shortPerson(tag.setBy) || 'an unknown person'}` : '';
    button = tag ? 'Mark active again' : 'Mark inactive';
  }

  return (
    <div className="client-flag-row">
      <span className={`client-flag-row__state${on ? ' client-flag-row__state--on' : ''}`}>
        {loading ? 'Reading' : state}
        {!loading && sub && <span className="client-flag-row__sub">{sub}</span>}
      </span>
      <Button size="sm" variant="outline-secondary" disabled={disabled || loading} onClick={() => setOpen(true)}>
        {button}
      </Button>
      <ClientTagDialog kind={kind} clientName={open ? clientName : null} onClose={() => setOpen(false)} />
    </div>
  );
}

export default ClientFlagControl;
